import { computed, MaybeRefOrGetter, toValue } from "vue";

import {
  AttachmentStatus,
  ATTACHMENT_THRESHOLDS,
  ATTACHMENT_STATUS_CONFIG,
} from "@/app/shared/utils/attachments";

interface UseAttachmentProgressOptions {
  maxSizeMB?: number;
}

const ONE_MB = 1024 * 1024;

export const useAttachmentProgress = (
  files: MaybeRefOrGetter<File[] | undefined>,
  options?: UseAttachmentProgressOptions
) => {
  const maxSizeMB = options?.maxSizeMB ?? 10;

  const totalBytes = computed(() => {
    return (toValue(files) || []).reduce((acc, file) => {
      return acc + (file?.size || 0);
    }, 0);
  });

  const totalMB = computed(() => {
    return totalBytes.value / ONE_MB;
  });

  const remainingMB = computed(() => {
    return Math.max(maxSizeMB - totalMB.value, 0);
  });

  const percentage = computed(() => {
    return Math.min(
      (totalMB.value / maxSizeMB) * 100,
      100
    );
  });

  const status = computed<AttachmentStatus>(() => {
    const currentPercentage = percentage.value;

    if (currentPercentage >= ATTACHMENT_THRESHOLDS.LIMIT) {
      return AttachmentStatus.LIMIT;
    }

    if (currentPercentage >= ATTACHMENT_THRESHOLDS.DANGER) {
      return AttachmentStatus.DANGER;
    }

    if (currentPercentage >= ATTACHMENT_THRESHOLDS.WARNING) {
      return AttachmentStatus.WARNING;
    }

    return AttachmentStatus.NORMAL;
  });

  const config = computed(() => {
    return ATTACHMENT_STATUS_CONFIG[status.value];
  });

  const formatMB = (value: number) => {
    return value.toFixed(1);
  };

  const statusMessage = computed(() => {
    const used = formatMB(totalMB.value);
    const remaining = formatMB(remainingMB.value);

    switch (status.value) {
      case AttachmentStatus.WARNING:
        return {
            prefix: "Te quedan",
            highlight: `${remaining}MB`,
            suffix: `(${used}MB usados)`,
        };

      case AttachmentStatus.DANGER:
        return {
            prefix: "Poco espacio",
            highlight: `${remaining}MB`,
            suffix: `(${used}MB usados)`,
        };

      case AttachmentStatus.LIMIT:
        return {
            prefix: "Alcanzaste el límite de",
            highlight: `${maxSizeMB}MB`,
            suffix: "",
        };

      default:
        return {
            prefix: "Te quedan",
            highlight: `${remaining}MB`,
            suffix: `(${used}MB usados)`,
        };
    }
  });

  const showProgress = computed(() => {
    return (toValue(files)?.length || 0) > 0;
  });

  return {
    percentage,
    totalBytes,
    totalMB,
    remainingMB,
    status,
    statusMessage,
    showProgress,
    config,
  };
};